import readXlsxFile, { readSheetNames } from "read-excel-file";
import type {
  GridDataItemRaw,
  GridDataItem,
  GridMappableColumn,
} from "@/types/grid";
import type {
  BulkImportChanges,
  ColumnMappingSuggestion,
  MapColumnFn,
  UploadedData,
  UploadedDataColumn,
  UploadedDataRow,
} from "@/types/bulk-import";

/**
 * Simplifies a column name by converting it to lowercase and removing spaces.
 */
function simplifyColumnName(columnName?: string) {
  return columnName?.toLowerCase().replace(/\s/g, "");
}

/**
 * Determines if a given column matches the configuration column. First by title, then by field
 *
 * @param configColumn - The configuration column to match against (only mappable columns with a field property).
 * @param columnName - The name of the column to check.
 * @param translatedTitle - The translated title of the column.
 * @returns True if the column matches, false otherwise.
 */
const isColumnMatch: MapColumnFn = (
  configColumn,
  columnName,
  translatedTitle,
) => {
  if (!columnName) {
    return false;
  }

  const simplifiedColumnName = simplifyColumnName(columnName);

  if (simplifiedColumnName === simplifyColumnName(translatedTitle)) {
    return true;
  }

  if (simplifiedColumnName === simplifyColumnName(configColumn.field)) {
    return true;
  }

  if (configColumn.alternativeMappingNames) {
    return configColumn.alternativeMappingNames.some((name) => {
      const simplifiedAlternativeName = simplifyColumnName(name);
      return simplifiedColumnName === simplifiedAlternativeName;
    });
  }

  return false;
};

/**
 * Maps columns from the source data to the grid configuration (only mappable columns with a field property).
 */
export function mapColumns<D extends GridDataItemRaw = GridDataItemRaw>(
  mappableColumns: GridMappableColumn<D>[],
  srcFirstRow: UploadedDataColumn[],
  t: (key: string) => string,
  isMatch?: MapColumnFn<D>,
): ColumnMappingSuggestion<D>[] {
  let columnsLeft = [...srcFirstRow];

  if (!mappableColumns.length || !columnsLeft.length) {
    return [];
  }

  const matchFn = isMatch ?? isColumnMatch;

  return mappableColumns.map((configColumn) => {
    const translatedTitle = configColumn.title
      ? t(configColumn.title)
      : undefined;
    const mappedColumn = columnsLeft.find(({ name, index }) =>
      matchFn(configColumn, name, translatedTitle, index),
    );

    if (!mappedColumn) {
      return {
        configColumn,
        mappedColumnIndex: undefined,
        mappedColumnName: "",
      };
    }

    columnsLeft = columnsLeft.filter(
      ({ index }) => index !== mappedColumn.index,
    );

    return {
      configColumn,
      mappedColumnIndex: mappedColumn.index,
      mappedColumnName: mappedColumn.name,
    };
  });
}

/**
 * Takes a source row and maps it to a grid data item, it uses a transform function for cases like dropdowns or dates.
 */
async function mapRow<D extends GridDataItemRaw>(
  srcRow: UploadedDataRow,
  mappedColumns: ColumnMappingSuggestion<D>[],
): Promise<Partial<D>> {
  return mappedColumns.reduce((acum, mappedColumn, index) => {
    if (!isMappableColumn(mappedColumn.configColumn)) {
      devConsole.error("field is not set for column", { index });
      return acum;
    }
    const field = mappedColumn.configColumn.field;
    if (mappedColumn.mappedColumnIndex === undefined) {
      devConsole.error("mappedColumnIndex is not set for column", field);
      return acum;
    }

    const rawContent = srcRow[mappedColumn.mappedColumnIndex];

    return {
      ...acum,
      [field]: rawContent,
    };
  }, {} as Partial<D>);
}

/**
 * Maps each uploaded data row to grid data item.
 */
export async function mapUploadedData<
  D extends GridDataItemRaw = GridDataItemRaw,
>(srcData: UploadedData, mappedColumns: ColumnMappingSuggestion<D>[]) {
  return Promise.all(srcData.map((srcRow) => mapRow<D>(srcRow, mappedColumns)));
}

async function getSheetByName(file: File, entityNames: string[]) {
  const normalizedNames = entityNames.map((s) => s.toLowerCase());
  const sheetNames = await readSheetNames(file);

  const closestSheetName =
    sheetNames.length === 1 || entityNames.length === 0
      ? sheetNames[0]
      : sheetNames.find((name) => normalizedNames.includes(name.toLowerCase()));

  if (!closestSheetName) return;

  return await readXlsxFile(file, { sheet: closestSheetName });
}

/**
 * Parses an Excel file and extracts the columns and data.
 *
 * @param file - The Excel file to parse.
 * @returns A promise that resolves to an object containing the source columns and data.
 * Source columns are an array of objects with the column name and index.
 */
export async function parseBulkFile(
  file: File,
  {
    entityNames = [],
    minColumnsCount = 2,
  }: {
    entityNames?: string[];
    minColumnsCount?: number;
  } = {},
) {
  try {
    const srcSheet = await getSheetByName(file, entityNames);
    if (!srcSheet) return;

    if (!srcSheet.length) {
      return { srcColumns: [], srcData: [] };
    }

    // Find the first row that contains data, which is considered the start of the table.
    // This is done by checking if the row has more than `minColumnsCount` non-empty cells.
    // This helps to skip any header or metadata rows that might be present before the actual data.
    const firstTableRowIndex = srcSheet.findIndex(
      (row) =>
        row.filter((cell) => cell?.toString()?.trim()?.length > 0).length >=
        minColumnsCount,
    );

    const [srcFirstRow, ...srcData] = srcSheet.slice(firstTableRowIndex);
    const srcColumns = srcFirstRow!.map((name, index) => ({
      name: name?.toString(),
      index,
    }));
    return { srcColumns, srcData: srcData as UploadedData };
  } catch (e) {
    devConsole.error("error parsing bulk file", e);
    throw new $ck.utils.UserFriendlyError(
      "cksetup.imports.import-into-table.parsingErrorMessage",
    );
  }
}

export function makeDraftSnapshot<D extends GridDataItemRaw = GridDataItemRaw>(
  dataItems: GridDataItem<D>[],
  originalItems: D[],
  uniqueKey?: StringKeyOf<D>[],
): BulkImportChanges | undefined {
  return makeDataChangesPayload(dataItems, originalItems, uniqueKey);
}

export function applyDraftSnapshot<D extends GridDataItemRaw = GridDataItemRaw>(
  draftSnapshot: BulkImportChanges<D>,
  currentItems: GridDataItem<D>[],
  uniqueKey?: StringKeyOf<D>[],
) {
  const { addedItems, updatedItems, deletedItems } = draftSnapshot;
  const upsertItems = [...updatedItems, ...addedItems];

  const newDeletedItems: Partial<D>[] = [];

  const getDirty = (original: Partial<D>, updated: Partial<D>) =>
    mapObjToRecord(
      { ...original, ...updated },
      (value, key) => !compareCellValues(value, original[key]),
    );

  const dataItems: GridDataItem<D>[] = $ck._.compact(
    $ck._.cloneDeep(currentItems).map((item) => {
      const { rawData } = getRawDataItem(item);

      if (
        deletedItems.some((deleted) =>
          compareItemsKey(deleted, item, uniqueKey),
        )
      ) {
        newDeletedItems.push(rawData);
        return;
      }

      // check changes not only for updated items but also for added items
      const updatedItem = upsertItems.find((updated) =>
        compareItemsKey(updated, item, uniqueKey),
      );

      if (updatedItem) {
        return {
          ...item,
          ...updatedItem,
          $dirty: getDirty(rawData, updatedItem),
        };
      }

      return item;
    }),
  );

  // Add new items
  addedItems.forEach((added) => {
    const isItemAlreadyAdded = dataItems.some((item) =>
      compareItemsKey(added, item, uniqueKey),
    );
    if (!isItemAlreadyAdded) {
      dataItems.push(
        makeGridDataItem<D>({
          ...added,
          $dirty: mapObjToRecord(added, true),
        }),
      );
    }
  });

  return {
    dataItems,
    deletedItems,
  };
}

export function hasBulkImportChanges(changes: BulkImportChanges) {
  return (
    changes?.addedItems.length ||
    changes?.updatedItems.length ||
    changes?.deletedItems.length
  );
}
